import { DestroyRef, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval, Subscription } from 'rxjs';
import { BannerService, BannerSettings } from '@components/layout/banner.service';

const CHECK_INTERVAL_MS = 10_000;

export interface StalenessConfig {
  /** Subject used in the default banner text, e.g. 'incident', 'brug', 'DRIP'. */
  label: string;
  /** Set for plural labels (e.g. 'wegafsluitingen') so the default message reads "Er zijn al …". */
  pluralLabel?: boolean;
  /** Newest update time (epoch millis) across the current data; 0 when unknown. */
  lastUpdatedTimestamp: () => number;
  maxAgeMinutesDaytime: number;
  maxAgeMinutesNighttime: number;
  /** Override the default `${label}`-based banner title/message when a source needs its own wording. */
  bannerTitle?: string;
  bannerMessage?: (minutesAgo: number) => string;
}

/** Epoch millis of the most recent timestamp among `items`, or 0 when none is known. */
export function maxTimestamp<TItem>(
  items: TItem[],
  select: (item: TItem) => string | number | undefined,
): number {
  // Single O(n) pass, no intermediate arrays: these lists can hold thousands of features and this
  // runs on every freshness tick. NaN (invalid dates) never wins the `>` comparison.
  let max = 0;
  for (const item of items) {
    const value = select(item);
    if (value == null) {
      continue;
    }
    const millis = typeof value === 'number' ? value : Date.parse(value);
    if (millis > max) {
      max = millis;
    }
  }
  return max;
}

/** Human-readable Dutch duration: minutes below an hour, hours below a day, days beyond. */
export function formatAge(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} minuten`;
  }
  if (minutes < 24 * 60) {
    return `meer dan ${Math.floor(minutes / 60)} uur`;
  }
  const days = Math.floor(minutes / (24 * 60));
  return `meer dan ${days} ${days === 1 ? 'dag' : 'dagen'}`;
}

export function fetchErrorBannerId(key: string): string {
  return `fetch-error-${key}`;
}

/** Banner shown when a snapshot/poll fails or a stream subscription errors. */
export function fetchErrorBanner(key: string, sourceLabel: string): BannerSettings {
  return {
    id: fetchErrorBannerId(key),
    title: `Probleem met ${sourceLabel}`,
    message: `Er ging iets mis bij het ophalen/verversen van ${sourceLabel}. Ververs de pagina of neem contact op met de servicedesk als het probleem blijft bestaan.`,
    type: 'critical',
  };
}

/**
 * Watches how long ago a data source last produced an update and shows a banner when it looks
 * stale. Reused by the streaming layers and the polling roadworks layer via {@link StalenessConfig}.
 */
export class LastUpdateAgeMonitor {
  private readonly bannerId: string;
  private readonly fetchErrorId: string;
  private subscription?: Subscription;

  constructor(
    key: string,
    private readonly config: StalenessConfig,
    private readonly bannerService: BannerService,
    private readonly destroyRef: DestroyRef,
  ) {
    this.bannerId = `stale-data-${key}`;
    this.fetchErrorId = fetchErrorBannerId(key);
  }

  start(): void {
    this.stop();
    this.subscription = interval(CHECK_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.check());
  }

  stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
    // stop() runs from stopListening(), which the map calls inside its layer-toggle effect.
    // closeBanner reads *and* writes the banners signal, so it must stay off the reactive graph,
    // otherwise that effect starts tracking banners and re-runs itself forever.
    untracked(() => this.bannerService.closeBanner(this.bannerId));
  }

  private check(): void {
    // Same reasoning as stop(): keep the read of the source and the banner writes untracked.
    untracked(() => {
      // A fetch error already tells the user the data is stale and is the more actionable message;
      // don't stack a staleness banner on top of it.
      if (this.bannerService.banners().some((banner) => banner.id === this.fetchErrorId)) {
        this.bannerService.closeBanner(this.bannerId);
        return;
      }

      // A single O(n) scan per 10s is cheap even for thousands of features.
      const lastUpdated = this.config.lastUpdatedTimestamp();
      // Nothing to compare against yet (no data, or the source omits its update time): stay silent.
      if (!lastUpdated) {
        this.bannerService.closeBanner(this.bannerId);
        return;
      }

      const millisSinceLastUpdate = Date.now() - lastUpdated;
      if (millisSinceLastUpdate > this.maxAgeMinutes * 60 * 1000) {
        const minutesAgo = Math.round(millisSinceLastUpdate / 1000 / 60);
        this.bannerService.addBanner({
          id: this.bannerId,
          title: this.config.bannerTitle ?? `Mogelijk probleem met ${this.config.label}data`,
          message:
            this.config.bannerMessage?.(minutesAgo) ??
            `Er ${this.config.pluralLabel ? 'zijn' : 'is'} al ${formatAge(minutesAgo)} geen ${this.config.label} geüpdatet. Ververs de pagina of neem contact op met de servicedesk als het probleem blijft bestaan.`,
          type: 'critical',
        });
      } else {
        this.bannerService.closeBanner(this.bannerId);
      }
    });
  }

  private get maxAgeMinutes(): number {
    const currentHour = new Date().getHours();
    return currentHour >= 21 || currentHour < 6
      ? this.config.maxAgeMinutesNighttime
      : this.config.maxAgeMinutesDaytime;
  }
}
